import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import { FulfillmentGroupStatus } from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import { RedoFulfillmentLocation } from "@redotech/redo-model/fulfillments/fulfillment-location";
import { ArrayStringFilterOperator } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import {
  DateFilterOperator,
  KnownDateFilterTimeFrame,
} from "@redotech/redo-model/views/advanced-filters/date-filter";
import { ExistsFilterOperator } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import { AdvancedFilterType } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import { AdvancedFilterData } from "@redotech/redo-model/views/views";
import {
  RedoIcon,
  RedoIconProps,
} from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Card } from "@redotech/redo-web/card";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { SkeletonText } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import * as styles from "./pick-sessions.module.css";

export const PickSessionMetrics = memo(function PickSessionMetrics({
  locationId,
  locations,
}: {
  locationId: string | null;
  locations: RedoFulfillmentLocation[];
}) {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useRequiredContext(TeamContext);

  const slaDateCountsLoad = useLoad(() => {
    const location = locations.find((loc) => loc._id === locationId);
    return rpcClient.getSlaDateCounts({
      merchantTimezone: team.ianaTimezone,
      locationId: location ? location.externalId : null,
    });
  }, [rpcClient, team, locationId]);

  const openOrders = useLoad(async () => {
    const advancedFiltersData: AdvancedFilterData[] = [
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [FulfillmentGroupStatus.Open],
        operator: ArrayStringFilterOperator.ANY_OF,
      },
      {
        name: "pickSessionId",
        type: AdvancedFilterType.EXISTS,
        value: null,
        operator: ExistsFilterOperator.NOT_EXISTS,
      },
    ];

    const location = locations.find((loc) => loc._id === locationId);
    if (location) {
      advancedFiltersData.push({
        name: "locationId",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [location.externalId],
        operator: ArrayStringFilterOperator.ANY_OF,
      });
    }

    return (
      await rpcClient.getFulfillmentGroupSummaryCount({
        advancedFiltersData,
        merchantTimezone: team.ianaTimezone,
      })
    ).total;
  }, [team, rpcClient, locationId]);

  const orderFulfilledToday = useLoad(async () => {
    const advancedFiltersData: AdvancedFilterData[] = [
      {
        name: "labelPurchasedDate",
        type: AdvancedFilterType.DATE,
        value: KnownDateFilterTimeFrame.TODAY,
        operator: DateFilterOperator.AFTER,
      },
    ];

    const location = locations.find((loc) => loc._id === locationId);
    if (location) {
      advancedFiltersData.push({
        name: "locationId",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [location.externalId],
        operator: ArrayStringFilterOperator.ANY_OF,
      });
    }

    return (
      await rpcClient.getFulfillmentGroupSummaryCount({
        advancedFiltersData,
        merchantTimezone: team.ianaTimezone,
      })
    ).total;
  }, [team, rpcClient, locationId]);

  return (
    <Flex className={styles.metricCardsContainer}>
      <PickMetricCard
        Icon={PackageIcon}
        pending={openOrders?.pending}
        subtext="orders ready to be shipped"
        title="Unfulfilled orders"
        value={openOrders?.value}
      />
      <PickMetricCard
        Icon={ClockIcon}
        pending={slaDateCountsLoad.pending}
        subtext="need to be shipped today"
        title="Orders due today"
        value={slaDateCountsLoad.value?.dueToday}
      />
      <PickMetricCard
        Icon={CheckCircleIcon}
        pending={orderFulfilledToday?.pending}
        subtext="orders shipped today"
        title="Orders fulfilled"
        value={orderFulfilledToday?.value}
      />
    </Flex>
  );
});

const PickMetricCard = memo(function PickMetricCard({
  title,
  value,
  pending,
  subtext,
  Icon,
}: {
  title: string;
  value: number | undefined;
  pending: boolean;
  subtext: string;
  Icon: RedoIconProps["Icon"];
}) {
  return (
    <Card borderColor="primary" p="md" radius="md" w="full">
      <Flex dir="column" gap="sm">
        <Flex align="center" justify="space-between">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {title}
          </Text>
          <RedoIcon color="secondary" Icon={Icon} size={14} />
        </Flex>
        <Text fontSize="lg" fontWeight="bold" textColor="primary">
          {pending ? <SkeletonText length={3} /> : value?.toLocaleString()}
        </Text>
        <Text fontSize="xxs" fontWeight="thin" textColor="quaternary">
          {subtext}
        </Text>
      </Flex>
    </Card>
  );
});
